/**
 * Liste des actions prioritaires + tons visuels alignés sur les badges métier (ex. risque traitement ROPA).
 */
import { risqueTraitementColor } from "./statusColors";

function variantIncidentGravite(gravite) {
  const m = {
    Critique: "gravite-inc-critique",
    Majeure: "gravite-inc-majeure",
    Modérée: "gravite-inc-moderee",
    Mineure: "gravite-inc-mineure",
  };
  return m[gravite] || "gravite-inc-moderee";
}

function variantRetraitStatut(statut) {
  return statut === "En cours de traitement" ? "retrait-encours" : "retrait-recue";
}

const E = risqueTraitementColor["Élevé"];
const C = risqueTraitementColor["Critique"];

/** Styles lignes tableau de bord (inline) ; classes CSS équivalentes : ta-notif-urgence.* */
export const priorityActionStyles = {
  "risque-eleve": {
    rowBg: "#fef2f2",
    rowBorder: E.border,
    pillBg: E.bg,
    pillColor: E.text,
    pillBorder: E.border,
  },
  "risque-critique": {
    rowBg: C.bg,
    rowBorder: C.border,
    pillBg: C.bg,
    pillColor: C.text,
    pillBorder: C.border,
  },
  critique: {
    rowBg: "#fef2f2",
    rowBorder: "#fca5a5",
    pillBg: "#fee2e2",
    pillColor: "#991b1b",
    pillBorder: "#fca5a5",
  },
  haute: {
    rowBg: "#fffbeb",
    rowBorder: "#fde68a",
    pillBg: "#fef3c7",
    pillColor: "#92400e",
    pillBorder: "#fcd34d",
  },
};

export function buildCriticalActions({ traitements, retraits, proofs, sousTraitants, incidents, droits }) {
  const now = new Date();
  return [
    ...traitements
      .filter((t) => !t.piaRealise && (t.risque === "Élevé" || t.risque === "Critique"))
      .map((t) => ({
        label: `Réaliser le PIA pour : ${t.nom}`,
        page: "ropa",
        module: "ROPA",
        badgeText: t.risque === "Critique" ? "Critique" : "Élevé",
        badgeTone: t.risque === "Critique" ? "risque-critique" : "risque-eleve",
        problemBadges: [{ variant: "ropa-pia-requis", text: "PIA requis" }],
        navContext: { id: t.id, itemType: "traitement" },
      })),
    ...traitements
      .filter((t) => t.transfertsHorsUE && (!t.transfertsDetail || t.transfertsDetail.includes("à activer") || t.transfertsDetail.includes("à réaliser")))
      .map((t) => ({
        label: `Sécuriser le transfert hors UE : ${t.nom}`,
        page: "ropa",
        module: "ROPA",
        badgeText: "Critique",
        badgeTone: "critique",
        problemBadges: [{ variant: "hors-ue", text: "Hors UE" }],
        navContext: { id: t.id, itemType: "traitement" },
      })),
    ...retraits
      .filter((r) => ["Reçue", "En cours de traitement"].includes(r.statut))
      .map((r) => ({
        label: `Traiter le retrait de consentement : ${r.personne}`,
        page: "consentement",
        module: "Consentements",
        badgeText: "Haute",
        badgeTone: "haute",
        problemBadges: [{ variant: variantRetraitStatut(r.statut), text: r.statut }],
        navContext: { id: r.id, itemType: "retrait" },
      })),
    ...proofs
      .filter((p) => p.statut === "Non conforme")
      .map((p) => ({
        label: `Corriger la preuve non conforme : ${p.intitule}`,
        page: "preuves",
        module: "Preuves",
        badgeText: "Haute",
        badgeTone: "haute",
        problemBadges: [{ variant: "preuve-non-conforme", text: "Non conforme" }],
        navContext: { id: p.id, itemType: "preuve" },
      })),
    ...sousTraitants
      .filter((s) => s.horsEEE && s.sccRequis && !["Activées (DPA)", "Activées (avenant)"].includes(s.sccStatut))
      .map((s) => ({
        label: `Activer les CCT avec ${s.nom} (transfert hors EEE)`,
        page: "registre-sous-traitants",
        module: "Sous-traitants",
        badgeText: "Critique",
        badgeTone: "critique",
        problemBadges: [
          { variant: "st-hors-eee", text: "Hors EEE" },
          { variant: "st-cct-activer", text: `CCT : ${s.sccStatut || "—"}` },
        ],
        navContext: { id: s.id, itemType: "sousTraitant" },
      })),
    ...incidents
      .filter((i) => !["Clôturé", "Sans suite"].includes(i.statut))
      .map((i) => {
        const g = i.gravite || "";
        if (g === "Critique") {
          return {
            label: `Clôturer l'incident : ${i.titre}`,
            page: "incidents",
            module: "Incidents",
            badgeText: "Critique",
            badgeTone: "risque-critique",
            problemBadges: [{ variant: variantIncidentGravite(g), text: i.gravite || "Critique" }],
            navContext: { id: i.id, itemType: "incident" },
          };
        }
        if (g === "Majeure") {
          return {
            label: `Clôturer l'incident : ${i.titre}`,
            page: "incidents",
            module: "Incidents",
            badgeText: "Majeure",
            badgeTone: "critique",
            problemBadges: [{ variant: variantIncidentGravite(g), text: i.gravite || "Majeure" }],
            navContext: { id: i.id, itemType: "incident" },
          };
        }
        return {
          label: `Clôturer l'incident : ${i.titre}`,
          page: "incidents",
          module: "Incidents",
          badgeText: "Haute",
          badgeTone: "haute",
          problemBadges: [{ variant: variantIncidentGravite(i.gravite || "Modérée"), text: i.gravite || "Modérée" }],
          navContext: { id: i.id, itemType: "incident" },
        };
      }),
    ...droits
      .filter((d) => d.dateEcheance && new Date(d.dateEcheance) < now && !["Complétée", "Refusée"].includes(d.statut))
      .map((d) => ({
        label: `Répondre hors délai : ${d.type}`,
        page: "droits",
        module: "Droits",
        badgeText: "Critique",
        badgeTone: "critique",
        problemBadges: [{ variant: "droit-hors-delai", text: "Hors délai" }],
        navContext: { id: d.id, itemType: "droit" },
      })),
    ...sousTraitants
      .filter((s) => s.dpaStatut !== "Signé" && s.dpaStatut !== "Non requis")
      .map((s) => ({
        label: `Signer le DPA avec ${s.nom}`,
        page: "registre-sous-traitants",
        module: "Sous-traitants",
        badgeText: "Haute",
        badgeTone: "haute",
        problemBadges: [{ variant: "st-dpa-signer", text: `DPA : ${s.dpaStatut}` }],
        navContext: { id: s.id, itemType: "sousTraitant" },
      })),
  ];
}
